import { STPlaceholderType, STSlideLayoutType, IPresentationData } from '@tencent/slide-sdk-model';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import { IThemeColorManager } from '@src/slide/application/service/theme-color-manager/index.interface';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import {
    ISlideRenderStateManager,
} from '../slide-render-state/slide-render-state-manager.interface';
import {
    ISlideContentLayoutAnalyzerService,
} from '../slide-content-layout-analyzer/core/slide-content-layout-analyzer-service.interface';
import {
    TextLeafNode,
} from '../slide-content-layout-analyzer/core/algorithm/data-structure/text-leaf-node';
import { TemplateContentAnalyzerService } from '@src/slide/application/service/template-content-analyzer-service';
import { IDynamicService } from '@src/slide/infrastructure/module/independent/dynamic-loader/dynamic-service';
import {
    ITemplateContentAnalyzerService,
} from '@src/slide/application/service/template-content-analyzer-service/index.interface';
import {
    ISlideContentEnumeratorService,
} from '@src/slide/application/service/slide-content-enumerator/slide-content-enumerator-service.interface';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { ITemplateFormatService } from '@src/slide/application/service/template-format-service/template-format-service.interface';
import {
    ITopic,
} from '@src/slide/application/service/change-template-service/component/template-select-list/template-topic-menu';
import { IAidOptions } from '@tencent/txdocs-aid';

// PaymentRequirementType 发货计价方式
export enum PaymentRequirementType {
    PaymentByPrice = 0, // 仅仅看价格
    LimitedFree = 1, // 限定条件的免费
    FreeForVIP = 2, // 会员免费
    NeedVIPAndPay = 3, // 需要会员付费才能使用
    Free = 4,
    // 超级会员免费
    FreeForSuperVIP = 5,

    ChargedByUsage = 6, // 按使用次数计费
    InAppPurchased = 7, // 应用内含付费
    NoRequirementType = 10, // 无类型，仅用于筛选
}

export const templateTagClassNames = {
    [PaymentRequirementType.FreeForVIP]: 'vip',
    [PaymentRequirementType.FreeForSuperVIP]: 'super-vip',
    [PaymentRequirementType.PaymentByPrice]: 'pay-by-price',
    [PaymentRequirementType.Free]: undefined,
    [PaymentRequirementType.LimitedFree]: undefined,
    [PaymentRequirementType.NeedVIPAndPay]: undefined,
    [PaymentRequirementType.ChargedByUsage]: undefined,
    [PaymentRequirementType.InAppPurchased]: undefined,
    [PaymentRequirementType.NoRequirementType]: undefined,
};
export interface ITemplateSelectListProps {
    changeTemplateService: IChangeTemplateService;
    selectedTemplateId: string;
    isLoading: boolean;
    onTemplateSelect(id: string, vipType: PaymentRequirementType): void;
}

export interface ImgInfo {
    url: string;
    height: number;
    width: number;
}

interface ITemplateSelectList {
    selectedTopicId: string;
    topicList: ITopic[],
}

export type ImgUrls = {
    [name in ImgUrlsKey]: ImgInfo;
};

export enum E_SELECTED_KEY {
    BIG = '0',
    SMALL = '1',
}
export enum ImgUrlsKey {
    M_2X = 'mobile_thumb_2x', // mobile缩略图
    PC_2X = 'pc_thumb_2x', // pc缩略图
    PC_PREVIEW = 'pc_preview', // pc端详情页预览图
}

export enum TemplateType {
    NoTemplateType = 0, // 没有类型. 作为过滤器, 用它来表示所有类型
    DocxTemplate = 1, // word模版
    SheetTemplate = 2, // excel模版
    PPTTemplate = 3, // ppt模板
    CollectionForm = 4, // 收集表模版
    MindTemplate = 5, // 思维导图
    FlowChartTemplate = 6, // 流程图
    AioTemplate = 7, // 小应用
    SmartSheet = 8, // 智能表格
    SmartCanvas = 9, // 智能画布
    Resume = 10, // 简历文档
    SmartWhiteBoard = 11, // 智能白板
}

export enum ItemType {
    NoType = 0,
    Template = 1,
    Addon = 2, // 文档插件
}

// RecommendTag 人工运营的推荐标签，这一类标签只关注文案，不支持搜索和聚类，它的设计要区别于tag和topic。如果要聚类，应该使用topic或者Tag
export interface MarketingItem {
    marketingItemId: string;
    itemId: string;
    tagIds: Array<string>;
    recommendTagIds?: Array<string>;
}

export interface Author {
    id: string; // 不是文档侧的tinyID, 因为不想和文档侧的id绑定等逻辑过度绑定. 希望这个id稳定不变. 文档测id在tiny_id字段
    name: string;
    iconUrl: string;
    desc: string;
    bannerImageUrls: Array<BannerImageIDPair>;
    websiteUrl: string;
    tinyId: string; // 该作者在其他平台的唯一id, 当前在文档系统就是文档侧的tiny id(文档用户唯一标识)
}

interface BannerImageIDPair {
    id: string; // 标识这是哪一类banner.后端不关注这个id具体是什么,管理端配置数据和使用的人才关注,后端只管保存
    imageUrl: string;
}

export interface SearchResult {
    offset: number;
    total: number;
    nextPagedownExtraInfo: string;
    marketingItems: MarketingItem[];
    authors: Author[];
    count: Count[];
    tags: Tag[];
    prices: PriceDetail[];
    items: Item[];
    recommendTags: RecommendTag[];
    topic: {
        name: string;
        id: string;
    };
}

export interface RecommendTag {
    id: string;
    name: string;
    imageUrl: string;
}


export interface PriceDetail {
    itemId: string;
    // int64 单位为分
    price: string;
    requirements: PaymentRequirement; // 付费要求
}

// PaymentRequirement 付费要求
export interface PaymentRequirement {
    type: PaymentRequirementType;
    limitation?: PaymentLimitation;
}

// PaymentLimitation 支付限定条件的具体限定数值
export interface PaymentLimitation {
    freeEndDate: string;
    freeStartDate: string; // int64免费到什么时候, unix time
}

// PaymentRequirement 付费要求
export interface PaymentRequirement {
    type: PaymentRequirementType;
    limitation?: PaymentLimitation;
}


// Tag 标签
export interface Tag {
    id: string;
    name: string;
}


// Count item相关的各种计数
export interface Count {
    id: TopicItem;
    use: number; // 使用次数
    view: number; // 浏览次数
    like: number; // 收藏者个数
}

export enum TopicItemType {
    SubTopic = 0,
    MarketingItemInTopic = 1,
    ArticleInTopic = 2,
    AuthorInTopic = 3,
}

// TopicItem topic里的一个item
export interface TopicItem {
    type: TopicItemType;
    itemId: string;
}

export interface Item {
    id: string;
    authorId: string;

    name: string;
    desc: string;
    content: any;
    source: ItemSource;

    type: ItemType;
    // meta_of_each_type 见具体item type的子目录，必然是个json。[注意]用字符串存放json，是经过了json编码的，注意反转。
    // 比如，model/mall/template/item.proto下的message MetaOfTemplate定义
    metaOfEachType: string;

    opRecord?: {
        createTime: string;
    };
}

export enum ItemSource {
    All = 0,
    Mall = 1,
    Custom = 2,
}


export interface MetaOfTemplate {
    images: ImgUrls;
    type: TemplateType;
    sub_type?: TemplateSubType;
    is_direct_create?: boolean; // 模板是否直接当前在页面创建（区别于进入详情页创建）
    meta_url?: string;
    decorations?: Decorations;// 文档装扮相关
}

export interface Thumbnail {
    images: ImgUrls;
}

export type Decorations = Array<any>;// 文档装扮相关;

// TemPlateSubType 模版子类型
export enum TemplateSubType {
    NoType = 0, // 没有类型. 作为过滤器, 用它来表示所有类型
    QuestionnaireTemplate = 1, // 收集表-收集表 模版
    CheckInTemplate = 2, // 收集表-打卡 模版
    ProblemSetTemplate = 3, // 收集表-习题 模版
    RosterTemplate = 4, // 收集表-接龙 模版
}

export interface ITemplateSelectListState {
    selectedTopicId: string;
    topicList: ITopic[];
    detailList: SearchResult[];
    searchKey: string;
}


export interface IChangeTemplateServiceProps {
    uiDataService: IUiDataService;
    themeColorManager: IThemeColorManager;
    selectionService: ISelectionService;
    slideRenderStateManager: ISlideRenderStateManager;
    contentLayoutAnalyzer: ISlideContentLayoutAnalyzerService;
    templateContentAnalyzerService: IDynamicService<ITemplateContentAnalyzerService>;
    slideContentEnumeratorService: ISlideContentEnumeratorService;
    templateFormatService: ITemplateFormatService;
}
export interface IChangeTemplateService {
    detailList: SearchResult[];
    selectedName?: string;
    dialogVisible: boolean;
    templateDataCached: Map<string, IPresentationData>;
    previewUiDataService: IUiDataService;
    hasCommentOnReplaceSlide?: boolean; // 替换的页面中是否有批注
    revertPreviewSlide(): Promise<IUiDataService>;
    previewBeautifyUi(
        templateId: string,
        presentationData: IPresentationData,
        isPreview: boolean,
    ): Promise<IUiDataService>;
    showTemplateBeautifyDialog(props: any): void;
    closeTemplateBeautifyDialog(): void;
    setDelegate(props: IChangeTemplateServiceProps): void;
    changeTemplate(presentationData: IPresentationData, isPreview?: boolean): void;
    createTemplateUiDataService(presentationData: IPresentationData): Promise<{
        cloneUiDataService: IUiDataService,
    }>;
    updateAid(aidOptions: IAidOptions);
    getPresentationByCached(templateId: string): Promise<IPresentationData>;
    updateTemplateToSlide(id): Promise<void>;
    getCoverSrc(metaData: Partial<MetaOfTemplate>): string;
}

export interface IRect {
    left: number;
    top: number;
    width: number;
    height: number;
}

export interface IBeautifiedSlideMapObj {
    index: number;
    templateIndex: number;
}

export interface IPlaceholderDescription {
    title: string;
    type?: STPlaceholderType;
}

export interface ITemplateDescription {
    standardTitle: string;
    alias?: string[];
    placeHolders?: IPlaceholderDescription[];
}

export interface IStandardTemplate {
    [STSlideLayoutType.title]: ITemplateDescription;
    [STSlideLayoutType.obj]: ITemplateDescription;
    [STSlideLayoutType.secHead]: ITemplateDescription;
    [STSlideLayoutType.twoObj]: ITemplateDescription;
    [STSlideLayoutType.twoTxTwoObj]: ITemplateDescription;
    [STSlideLayoutType.titleOnly]: ITemplateDescription;
    [STSlideLayoutType.blank]: ITemplateDescription;
    [STSlideLayoutType.objTx]: ITemplateDescription;
    [STSlideLayoutType.picTx]: ITemplateDescription;
    [STSlideLayoutType.vertTx]: ITemplateDescription;
    [STSlideLayoutType.vertTitleAndTx]: ITemplateDescription;
}
